import {
  useState, useMemo, useEffect, useRef,
  type ReactNode, type Key,
} from 'react';

/* ============================================================================
   DataTable — the one table primitive for the whole app.

   Desktop  → an enterprise table.
   Phone    → the SAME data as cards. No horizontal scrolling, ever.

   That transform is done in CSS (see responsive.css), not by rendering two
   different trees: one DOM, one source of truth, so the two can never drift.
   Each <td> carries data-label, which becomes the field name on a card.

   Built in because every listing page needs them and shouldn't reinvent them:
   debounced search, multi-column matching + highlighting, sorting, pagination
   (client- or server-driven), loading skeletons, and a real empty state.
   ========================================================================== */

export interface Column<T> {
  /** Stable key. Also the default sort field. */
  id: string;
  header: ReactNode;
  /** Cell contents. */
  cell: (row: T) => ReactNode;
  /** Plain text used for searching, sorting, and the mobile label. */
  value?: (row: T) => string | number | null | undefined;
  /** The card's title on mobile — exactly one column should set this. */
  primary?: boolean;
  /** Renders as the card's action footer on mobile. */
  actions?: boolean;
  sortable?: boolean;
  align?: 'left' | 'right';
  /** Hide below 900px / 640px — for columns that aren't worth the space. */
  hideMd?: boolean;
  hideSm?: boolean;
}

interface Props<T> {
  rows: T[] | null;                      // null = loading
  columns: Column<T>[];
  rowKey: (row: T) => Key;

  /** Search box. Omit `onSearch` to filter client-side automatically. */
  search?: boolean;
  searchPlaceholder?: string;
  onSearch?: (q: string) => void;        // server-side search (debounced)

  /** Pagination. Omit `total` to paginate client-side. */
  pageSize?: number;
  total?: number;                        // server-side: total row count
  page?: number;                         // server-side: current page (1-based)
  onPage?: (page: number, size: number) => void;

  onRowClick?: (row: T) => void;
  filters?: ReactNode;                   // chips etc., sit beside the search box
  empty?: { title: string; body?: string; action?: ReactNode };
  /** Skeleton row count while loading. */
  skeletonRows?: number;
}

const SIZES = [10, 25, 50, 100];

export function DataTable<T>({
  rows, columns, rowKey,
  search, searchPlaceholder = 'Search…', onSearch,
  pageSize: initialSize = 25, total, page: serverPage, onPage,
  onRowClick, filters, empty, skeletonRows = 6,
}: Props<T>) {
  const serverMode = typeof total === 'number';

  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [sort, setSort] = useState<{ id: string; dir: 'asc' | 'desc' } | null>(null);
  const [clientPage, setClientPage] = useState(1);
  const [size, setSize] = useState(initialSize);

  // Debounce the search box. Typing must feel instant; the *query* shouldn't
  // fire on every keystroke.
  const firstRun = useRef(true);
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedQ(q);
      setClientPage(1);
      if (onSearch) {
        // don't re-issue the caller's initial load on mount
        if (firstRun.current && q === '') { firstRun.current = false; return; }
        firstRun.current = false;
        onSearch(q);
      }
    }, 250);
    return () => clearTimeout(t);
    // onSearch is intentionally excluded: callers commonly pass an inline
    // lambda, which would re-fire the effect on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const text = (row: T, c: Column<T>): string => {
    const v = c.value ? c.value(row) : undefined;
    return v == null ? '' : String(v);
  };

  // ---- client-side search + sort (skipped entirely in server mode) ----
  const processed = useMemo(() => {
    if (!rows) return null;

    // ⚠ DEFENSIVE, and it earned its place.
    //
    // A paged endpoint returns { items, total }. When a caller forgets to unwrap it, `rows` is an
    // object, `.slice` is not a function, and React unmounts the whole page — a WHITE SCREEN, with a
    // minified stack trace as the only clue.
    //
    // That is a wildly disproportionate failure for one missed `.items`, and it happened. Accepting
    // the wrapped shape here turns a dead page into a working one, and the console line tells
    // whoever is looking exactly what to fix.
    if (!Array.isArray(rows)) {
      const maybe = (rows as unknown as { items?: unknown }).items;
      if (Array.isArray(maybe)) {
        console.warn(
          'DataTable: received a paged response instead of an array. ' +
          'Pass `response.items` rather than `response` — unwrapping it here as a fallback.',
        );
        return maybe as typeof rows;
      }
      console.error('DataTable: `rows` is neither an array nor a paged response.', rows);
      return [];
    }

    let out = rows;

    if (!onSearch && debouncedQ.trim()) {
      const needle = debouncedQ.trim().toLowerCase();
      out = out.filter((r) =>
        columns.some((c) => text(r, c).toLowerCase().includes(needle)));
    }

    if (sort) {
      const col = columns.find((c) => c.id === sort.id);
      if (col) {
        out = [...out].sort((a, b) => {
          const av = col.value ? col.value(a) : '';
          const bv = col.value ? col.value(b) : '';
          // numbers sort numerically; everything else lexically
          const cmp = typeof av === 'number' && typeof bv === 'number'
            ? av - bv
            : String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true });
          return sort.dir === 'asc' ? cmp : -cmp;
        });
      }
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, debouncedQ, sort, columns, onSearch]);

  const page = serverMode ? (serverPage ?? 1) : clientPage;
  const count = serverMode ? (total as number) : (processed?.length ?? 0);
  const pages = Math.max(1, Math.ceil(count / size));

  const visible = useMemo(() => {
    if (!processed) return null;
    if (serverMode) return processed;            // server already sliced it
    const start = (clientPage - 1) * size;
    return processed.slice(start, start + size);
  }, [processed, clientPage, size, serverMode]);

  const goto = (p: number) => {
    const next = Math.min(Math.max(1, p), pages);
    if (serverMode) onPage?.(next, size);
    else setClientPage(next);
  };

  const changeSize = (s: number) => {
    setSize(s);
    if (serverMode) onPage?.(1, s);
    else setClientPage(1);
  };

  const toggleSort = (c: Column<T>) => {
    if (!c.sortable) return;
    setSort((cur) =>
      cur?.id !== c.id ? { id: c.id, dir: 'asc' }
        : cur.dir === 'asc' ? { id: c.id, dir: 'desc' }
          : null);                                // third click clears it
  };

  const loading = rows === null;
  const isEmpty = !loading && (visible?.length ?? 0) === 0;

  return (
    <div>
      {(search || filters) && (
        <div className="toolbar">
          {search && (
            <div className="toolbar-search">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" />
              </svg>
              <input
                className="input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
              />
              {q && (
                <button className="clear" onClick={() => setQ('')} aria-label="Clear search">✕</button>
              )}
            </div>
          )}
          {filters && <div className="toolbar-filters">{filters}</div>}
        </div>
      )}

      <div className="dt-wrap">
        <table className="dt">
          <thead>
            <tr>
              {columns.map((c) => (
                <th
                  key={c.id}
                  className={[
                    c.sortable ? 'is-sortable' : '',
                    c.align === 'right' ? 'dt-num' : '',
                    c.hideMd ? 'dt-hide-md' : '',
                    c.hideSm ? 'dt-hide-sm' : '',
                  ].filter(Boolean).join(' ')}
                  onClick={() => toggleSort(c)}
                  aria-sort={sort?.id === c.id ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                >
                  {c.header}
                  {c.sortable && (
                    <span className={`dt-sort ${sort?.id === c.id ? 'is-on' : ''}`}>
                      {sort?.id === c.id ? (sort.dir === 'asc' ? '▲' : '▼') : '▲'}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading && Array.from({ length: skeletonRows }).map((_, i) => (
              <tr className="sk-row" key={`sk${i}`}>
                {columns.map((c) => (
                  <td key={c.id} data-label={labelOf(c)}>
                    <span className="sk" style={{ width: `${45 + ((i * 13 + c.id.length * 7) % 45)}%` }} />
                  </td>
                ))}
              </tr>
            ))}

            {!loading && visible?.map((row) => (
              <tr
                key={rowKey(row)}
                className={onRowClick ? 'is-clickable' : ''}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {columns.map((c) => (
                  <td
                    key={c.id}
                    data-label={labelOf(c)}
                    className={[
                      c.primary ? 'dt-title' : '',
                      c.actions ? 'dt-actions' : '',
                      c.align === 'right' ? 'dt-num' : '',
                      c.hideMd ? 'dt-hide-md' : '',
                      c.hideSm ? 'dt-hide-sm' : '',
                    ].filter(Boolean).join(' ')}
                    // stop an action button inside a clickable row from also firing the row
                    onClick={c.actions ? (e) => e.stopPropagation() : undefined}
                  >
                    {highlight(c.cell(row), c, onSearch ? '' : debouncedQ)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>

        {isEmpty && (
          <div className="empty">
            <svg className="empty-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" strokeLinecap="round" />
            </svg>
            <h3>{debouncedQ ? 'No matches' : (empty?.title ?? 'Nothing here yet')}</h3>
            <p>
              {debouncedQ
                ? <>Nothing matches “{debouncedQ}”. Try a different search.</>
                : (empty?.body ?? 'Records will appear here once they’re added.')}
            </p>
            {!debouncedQ && empty?.action}
          </div>
        )}

        {!isEmpty && !loading && count > 0 && (
          <div className="dt-foot">
            <span className="dt-foot-count">
              {serverMode
                ? <>Showing {(page - 1) * size + 1}–{Math.min(page * size, count)} of {count}</>
                : <>{count} {count === 1 ? 'record' : 'records'}</>}
            </span>

            <select
              className="pg-size"
              value={size}
              onChange={(e) => changeSize(Number(e.target.value))}
              aria-label="Rows per page"
            >
              {SIZES.map((s) => <option key={s} value={s}>{s} / page</option>)}
            </select>

            {pages > 1 && (
              <div className="dt-foot-right">
                <button className="pg-btn" onClick={() => goto(page - 1)} disabled={page <= 1} aria-label="Previous page">‹</button>
                {pageList(page, pages).map((p, i) =>
                  p === '…'
                    ? <span className="pg-gap" key={`g${i}`}>…</span>
                    : <button
                        key={p}
                        className={`pg-btn ${p === page ? 'is-on' : ''}`}
                        onClick={() => goto(p as number)}
                        aria-current={p === page ? 'page' : undefined}
                      >{p}</button>,
                )}
                <button className="pg-btn" onClick={() => goto(page + 1)} disabled={page >= pages} aria-label="Next page">›</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ utils */

/** The mobile field label: a column's header, as plain text. */
function labelOf<T>(c: Column<T>): string {
  return typeof c.header === 'string' ? c.header : c.id;
}

/** Wrap matches in <mark> — only for plain-text cells, so we never mangle JSX. */
function highlight<T>(node: ReactNode, c: Column<T>, q: string): ReactNode {
  if (!q.trim() || !c.value) return node;
  if (typeof node !== 'string' && typeof node !== 'number') return node;

  const s = String(node);
  const i = s.toLowerCase().indexOf(q.trim().toLowerCase());
  if (i < 0) return node;
  const len = q.trim().length;
  return (
    <>
      {s.slice(0, i)}
      <mark>{s.slice(i, i + len)}</mark>
      {s.slice(i + len)}
    </>
  );
}

/** 1 … 4 5 [6] 7 8 … 20 — always ≤ 7 controls, so it fits a phone. */
function pageList(page: number, pages: number): (number | '…')[] {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  if (page <= 4) return [1, 2, 3, 4, 5, '…', pages];
  if (page >= pages - 3) return [1, '…', pages - 4, pages - 3, pages - 2, pages - 1, pages];
  return [1, '…', page - 1, page, page + 1, '…', pages];
}
